import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Fingerprint, MapPin, Globe, ArrowRight, Search,
  FileCheck, Gem, Sparkles, Building2, Mountain, Leaf, Citrus, Package,
} from "lucide-react";
import { Reveal, Eyebrow } from "@/components/ui-bits";
import { EditorialSplit } from "@/components/PageShell";
import { IMG } from "@/data/images";
import { api } from "@/lib/api";

export default function Home() {
  const [cert, setCert] = useState(null);
  const [lookup, setLookup] = useState("");
  const navigate = useNavigate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/certificate/demo").then((r) => setCert(r.data)).catch(() => {}); }, []);

  const runLookup = () => {
    const v = lookup.trim();
    navigate(v ? `/verify?bb=${encodeURIComponent(v)}` : "/verify");
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden grain">
        <div className="absolute inset-0 radial-blue" />
        <img src={IMG.landTexture} alt="" className="absolute inset-0 h-full w-full object-cover opacity-[0.12]" />
        <div className="absolute inset-0 bg-gradient-to-b from-bb-bg/60 via-bb-bg/80 to-bb-bg" />

        <div className="relative z-10 mx-auto max-w-[1400px] w-full px-6 lg:px-10 pt-32 pb-16 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <Eyebrow>Verified Origin for Botswana's Exports</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-8 font-serif font-light text-6xl sm:text-7xl lg:text-[80px] tracking-tight leading-[1.04]">
                Every Botswana product<br />
                <span className="gold-text italic">deserves an identity.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-9 text-lg md:text-xl font-light text-bb-silver/75 max-w-xl leading-relaxed">
                Botswana Brilliance gives every Botswana product a verified digital identity, connecting its physical
                identity, Botswana origin and documented journey to international markets in one secure certificate.
                We begin with diamonds.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/verify" data-testid="hero-verify-btn" className="btn-gold rounded-full px-7 py-3.5 text-sm flex items-center gap-2">
                  <Search className="h-4 w-4" strokeWidth={1.4} /> Verify a Diamond
                </Link>
                <Link to="/portal/submit" data-testid="hero-submit-btn" className="btn-ghost rounded-full px-7 py-3.5 text-sm flex items-center gap-2">
                  Register Rough or Submit Polished
                </Link>
                <Link to="/certificate" data-testid="hero-explore-btn" className="btn-ghost rounded-full px-7 py-3.5 text-sm flex items-center gap-2">
                  Explore the Certificate
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.4}>
              <p className="mt-10 text-sm text-bb-silver/50 italic max-w-md leading-relaxed">
                One verified identity, from Botswana rough to polished brilliance.
              </p>
            </Reveal>
          </div>

          {/* Floating diamond + identity ring (preserved) */}
          <div className="lg:col-span-6 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative mx-auto max-w-md aspect-square"
            >
              <div className="absolute inset-0 rounded-full border border-bb-gold/15 animate-spin-slow" />
              <div className="absolute inset-8 rounded-full border border-white/8" />
              <div
                className="absolute inset-6 rounded-full overflow-hidden animate-float"
                style={{ background: "radial-gradient(circle at 50% 46%, rgba(16,42,78,0.7) 0%, rgba(5,11,20,0) 68%)" }}
              >
                <img
                  src={IMG.heroDiamond}
                  alt="Exceptional Botswana diamond"
                  style={{ mixBlendMode: "screen" }}
                  className="h-full w-full object-cover scale-125 drop-shadow-[0_0_80px_rgba(198,168,124,0.3)]"
                />
              </div>
              {[
                { icon: MapPin, label: "Origin", pos: "top-2 left-1/2 -translate-x-1/2" },
                { icon: Fingerprint, label: "Identity", pos: "top-1/3 -right-2" },
                { icon: ShieldCheck, label: "Verified", pos: "bottom-1/4 -right-3" },
                { icon: Globe, label: "Journey", pos: "bottom-2 left-1/2 -translate-x-1/2" },
                { icon: Sparkles, label: "Pride Mark", pos: "top-1/3 -left-3" },
              ].map((n, i) => (
                <motion.div
                  key={n.label}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 + i * 0.15, duration: 0.8 }}
                  className={`absolute ${n.pos} glass rounded-full px-3 py-1.5 flex items-center gap-1.5`}
                >
                  <n.icon className="h-3.5 w-3.5 text-bb-gold" strokeWidth={1.3} />
                  <span className="text-[0.62rem] tracking-wide text-bb-silver">{n.label}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-xs tracking-[0.3em] text-bb-silver/40 uppercase">
          One origin · One verified journey · One certificate
        </div>
      </section>

      {/* STATS BAR */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-12 gap-x-8 py-20 border-y border-white/10">
          {[
            ["6", "Verified stages, from Botswana source to certificate"],
            ["1", "Living digital certificate uniting identity, origin and journey"],
            ["100%", "Recorded diamonds begin with lawful Botswana documentation"],
            ["24 / 7", "Public verification by QR code or Botswana Brilliance number"],
          ].map(([n, l], i) => (
            <Reveal key={l} delay={i * 0.08}>
              <div>
                <div className="font-serif font-light text-5xl md:text-6xl lg:text-7xl tracking-tight gold-text">{n}</div>
                <div className="mt-4 text-sm text-bb-silver/60 leading-relaxed max-w-[15rem]">{l}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* THREE IDEAS: Beauty / Proof / Purpose */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 md:py-32">
        <Reveal><Eyebrow>The Experience</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl lg:text-6xl tracking-tight max-w-3xl leading-[1.08]">
            Beauty, proof and purpose, held within a single record.
          </h2>
        </Reveal>
        <div className="mt-16 grid md:grid-cols-3 gap-6">
          {[
            { icon: Gem, t: "Beauty", d: "The extraordinary natural diamond. Its light, its rarity, its presence." },
            { icon: FileCheck, t: "Proof", d: "Its identity, origin and documented journey, connected and independently supported." },
            { icon: Building2, t: "Purpose", d: "The people, landscapes and living heritage connected to its value." },
          ].map((c, i) => (
            <Reveal key={c.t} delay={i * 0.12}>
              <div className="glass rounded-[2rem] p-10 md:p-12 h-full hover:border-bb-gold/25 transition-colors duration-500">
                <c.icon className="h-8 w-8 text-bb-gold" strokeWidth={1} />
                <h3 className="mt-8 font-serif font-light text-3xl">{c.t}</h3>
                <p className="mt-4 text-base text-bb-silver/65 leading-relaxed">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* YOUR DIAMOND, VISIBLE — ID lookup block */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 pb-24 md:pb-32">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] border border-white/10">
            <div className="absolute inset-0 radial-blue" />
            <div className="absolute inset-0 bg-gradient-to-r from-bb-bg via-bb-bg/85 to-bb-bg/60" />
            <div className="relative z-10 px-8 md:px-16 py-16 md:py-24 max-w-2xl">
              <Eyebrow>Your Diamond, Visible</Eyebrow>
              <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.08]">
                Enter a number. See the whole journey.
              </h2>
              <p className="mt-6 text-base md:text-lg font-light text-bb-silver/75 leading-relaxed">
                Enter a Botswana Brilliance number or scan the certificate QR code to open the living record: its Botswana
                origin, rough registration, documented journey and current verification status.
              </p>
              <div className="mt-9 glass rounded-full p-1.5 flex items-center gap-2 max-w-lg">
                <Search className="h-5 w-5 text-bb-silver/50 ml-4" strokeWidth={1.3} />
                <input
                  data-testid="home-lookup-input"
                  value={lookup}
                  onChange={(e) => setLookup(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && runLookup()}
                  placeholder="Enter BB certificate number..."
                  className="flex-1 bg-transparent outline-none text-bb-white placeholder:text-bb-silver/40 py-3 tracking-wide font-mono text-sm"
                />
                <button data-testid="home-lookup-btn" onClick={runLookup} className="btn-gold rounded-full px-6 py-2.5 text-sm whitespace-nowrap">
                  Verify
                </button>
              </div>
              <div className="mt-4 text-xs text-bb-silver/45">Try <span className="font-mono text-bb-gold/80">BB-BW-2026-000117</span></div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* CERTIFICATE PREVIEW */}
      <section className="relative py-24 md:py-32 border-y border-white/8 bg-[#040910]">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <Reveal><Eyebrow>The Living Digital Passport</Eyebrow></Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl lg:text-6xl tracking-tight max-w-2xl leading-[1.08]">
                  The Botswana Brilliance Certificate
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <Link to="/certificate" className="btn-ghost rounded-full px-6 py-3 text-sm flex items-center gap-2 whitespace-nowrap">
                Explore the full certificate <ArrowRight className="h-4 w-4" strokeWidth={1.4} />
              </Link>
            </Reveal>
          </div>

          {cert && (
            <Reveal delay={0.15}>
              <CertificatePreview cert={cert} />
            </Reveal>
          )}
        </div>
      </section>

      {/* EDITORIAL SPLIT: rough to polished */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 md:py-32">
        <EditorialSplit
          eyebrow="Rough to Polished"
          title="One identity, carried from the earth to the hand."
          image={IMG.heroDiamond}
          imageAlt="Botswana diamond"
          cta={
            <Link to="/certificate" className="mt-10 inline-flex items-center gap-2 text-bb-gold hover:gap-3 transition-all duration-300 text-sm tracking-wide">
              Follow the six-stage journey <ArrowRight className="h-4 w-4" strokeWidth={1.4} />
            </Link>
          }
        >
          <p>
            A Botswana rough diamond can be registered before manufacturing and given a unique Botswana Brilliance Rough ID.
            As it is cut and polished, its records travel with it, and each polished diamond can be connected back to the
            rough it came from.
          </p>
          <p>
            The result is a single, verifiable identity: Botswana origin, documented custody, manufacturing and grading, all
            held within one living certificate rather than a stack of separate papers.
          </p>
        </EditorialSplit>
      </section>

      {/* FROM THE EARTH — tree image behind the words */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 pb-24 md:pb-32">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] border border-white/10 min-h-[520px] flex items-center">
            <img
              src={IMG.okavangoAerial}
              alt="Okavango waterways, Botswana"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-bb-bg via-bb-bg/85 to-bb-bg/35" />
            <div className="absolute inset-0 bg-bb-bg/25" />
            <div className="relative z-10 px-8 md:px-16 py-16 md:py-24 max-w-2xl">
              <Eyebrow>From the Earth, Value</Eyebrow>
              <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.08]">
                A diamond shaped by the land. A legacy shared with its people.
              </h2>
              <p className="mt-8 text-base md:text-lg font-light text-bb-silver/85 leading-relaxed">
                Born beneath one of Africa's most extraordinary landscapes, a Botswana diamond carries a significance
                extending far beyond its rarity. Its value forms part of a wider legacy, supporting human potential,
                strengthening communities and advancing the responsible stewardship of the land from which it came.
              </p>
              <Link to="/journey" className="mt-10 inline-flex items-center gap-2 text-bb-gold hover:gap-3 transition-all duration-300 text-sm tracking-wide">
                The Botswana Journey <ArrowRight className="h-4 w-4" strokeWidth={1.4} />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      {/* BEYOND DIAMONDS */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 pb-24 md:pb-32">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div>
            <Reveal><Eyebrow>One Platform, Every Botswana Export</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif font-light text-4xl md:text-5xl lg:text-6xl tracking-tight max-w-3xl leading-[1.08]">
                From diamonds, to everything Botswana exports.
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <p className="text-sm text-bb-silver/60 max-w-sm leading-relaxed">
              Botswana Brilliance is the trust infrastructure connecting Botswana-origin goods to international markets.
              The same living digital certificate is built to carry any Botswana export that deserves a documented origin,
              beginning with diamonds.
            </p>
          </Reveal>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: Gem, t: "Diamonds", d: "Origin, identity and journey in one living certificate.", tag: "Available now" },
            { icon: Mountain, t: "Minerals", d: "Responsibly sourced mineral wealth, documented end to end.", tag: "Next" },
            { icon: Citrus, t: "Agriculture & Produce", d: "From citrus to produce, provenance buyers can trust.", tag: "On the roadmap" },
            { icon: Leaf, t: "Leather & Craft", d: "Botswana craftsmanship, carried with its verified story.", tag: "On the roadmap" },
          ].map((c, i) => (
            <Reveal key={c.t} delay={i * 0.1}>
              <div className={`glass rounded-[2rem] p-8 h-full transition-colors duration-500 ${i === 0 ? "border-bb-gold/30" : "hover:border-bb-gold/20"}`}>
                <div className="flex items-center justify-between">
                  <c.icon className="h-7 w-7 text-bb-gold" strokeWidth={1} />
                  <span className={`text-[0.6rem] uppercase tracking-[0.2em] rounded-full px-2.5 py-1 border ${i === 0 ? "border-bb-gold/40 text-bb-gold bg-bb-gold/5" : "border-white/12 text-bb-silver/50"}`}>{c.tag}</span>
                </div>
                <h3 className="mt-6 font-serif font-light text-2xl">{c.t}</h3>
                <p className="mt-3 text-sm text-bb-silver/60 leading-relaxed">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-8 flex items-center gap-2 text-xs text-bb-silver/45">
            <Package className="h-4 w-4 text-bb-silver/40" strokeWidth={1.2} />
            Any Botswana product seeking traceability can be onboarded onto the same secure certificate architecture.
          </p>
        </Reveal>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 md:py-32">
        <Reveal>
          <div className="glass rounded-[2.5rem] p-12 md:p-24 text-center relative overflow-hidden">
            <div className="absolute inset-0 radial-blue" />
            <div className="relative z-10">
              <h2 className="font-serif font-light text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.08] max-w-3xl mx-auto">
                The diamond remains yours.<br /><span className="gold-text italic">Its story becomes verifiable.</span>
              </h2>
              <div className="mt-12 flex flex-wrap justify-center gap-3">
                <Link to="/portal/submit" className="btn-gold rounded-full px-8 py-3.5 text-sm">Register Rough or Submit Polished</Link>
                <Link to="/contact" className="btn-ghost rounded-full px-8 py-3.5 text-sm">Become a Participant</Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}

function CertificatePreview({ cert }) {
  const pi = cert.physical_identity || {};
  return (
    <div className="grid md:grid-cols-12 gap-6">
      <div className="md:col-span-4 glass rounded-[2rem] overflow-hidden">
        <div className="relative aspect-[4/3] bg-gradient-to-b from-[#0A1A30] to-black flex items-center justify-center">
          <div className="absolute inset-0 radial-blue" />
          <img src={(cert.media?.images || [])[0]} alt={cert.name} style={{ mixBlendMode: "screen" }} className="relative z-10 w-2/3 h-2/3 object-contain animate-float" />
        </div>
        <div className="p-8">
          <div className="font-serif font-light text-2xl">{cert.name}</div>
          <div className="font-mono text-sm text-bb-gold mt-1">{cert.bb_number}</div>
        </div>
      </div>
      <div className="md:col-span-8 glass rounded-[2rem] p-10">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[["Carat", pi.carat], ["Colour", pi.color], ["Clarity", pi.clarity], ["Cut", pi.cut]].map(([k, v]) => (
            <div key={k}>
              <div className="font-serif font-light text-3xl">{v}</div>
              <div className="label-eyebrow mt-1">{k}</div>
            </div>
          ))}
        </div>
        <div className="hairline my-8" />
        <div className="grid sm:grid-cols-3 gap-4">
          {(cert.journey || []).slice(0, 6).map((s) => (
            <div key={s.key} className="flex items-center gap-2">
              <span className={`h-1.5 w-1.5 rounded-full ${s.status === "not_selected" ? "bg-white/20" : "bg-bb-gold"}`} />
              <span className="text-sm text-bb-silver/70">{s.title}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
